import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Gauge, RefreshCw, Database, Flame, Loader2 } from "lucide-react";

export function AdminPerf() {
  const [hours, setHours] = useState("24");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [warm, setWarm] = useState({ targets: [], last_run: null, last_result: null, finished_at: null });
  const [warming, setWarming] = useState(false);

  const load = () => {
    api.get("/perf/summary", { params: { hours: Number(hours) } }).then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/perf/recent", { params: { limit: 30 } }).then(({ data }) => setRecent(data)).catch(() => {});
    api.get("/perf/warm-status").then(({ data }) => setWarm(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);

  const warmNow = async () => {
    setWarming(true);
    try {
      const { data } = await api.post("/perf/warm-now");
      setWarm((w) => ({ ...w, last_run: new Date().toISOString(), last_result: data.last_result, finished_at: data.finished_at }));
      load();
    } catch (_e) { /* toast handled by axios */ }
    finally { setWarming(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-perf">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[color:var(--ink)] flex items-center gap-2"><Gauge className="w-4 h-4 text-[color:var(--brand-ocean)]" />Query performance analyzer</h3>
        <div className="flex items-center gap-2">
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="h-8 w-32 text-xs bg-[color:var(--surface)]" data-testid="perf-window"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="24">Last 24 hours</SelectItem><SelectItem value="72">Last 3 days</SelectItem><SelectItem value="168">Last 7 days</SelectItem></SelectContent>
          </Select>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Queries", summary?.total ?? "—"], ["Avg latency", summary ? `${summary.avg_ms} ms` : "—"], ["p95 latency", summary ? `${summary.p95_ms} ms` : "—"], ["Cache hit rate", summary ? `${summary.cache_hit_rate}%` : "—"]].map(([l, v]) => (
          <div key={l} className="glass-card p-3" data-testid="perf-stat"><div className="font-display text-xl text-[color:var(--ink)] tabular-nums">{v}</div><div className="text-[11px] text-[color:var(--ink-3)]">{l}</div></div>
        ))}
      </div>

      {summary?.engines?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-[color:var(--ink-3)]">Engines:</span>
          {summary.engines.map((e) => <Badge key={e.engine} variant="outline" className="text-[10px]">{e.engine} · {e.count}</Badge>)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card p-4">
          <h4 className="text-xs font-semibold text-[color:var(--ink)] mb-2 flex items-center gap-1.5"><Database className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />Slowest datasets (avg)</h4>
          <table className="w-full text-xs" data-testid="perf-slowest-table">
            <thead><tr className="text-left text-[color:var(--ink-3)] border-b border-[color:var(--glass-border)]"><th className="py-1.5">Dataset</th><th className="py-1.5">Queries</th><th className="py-1.5">Avg</th><th className="py-1.5">Max</th></tr></thead>
            <tbody>
              {(summary?.slowest_datasets || []).map((d) => (
                <tr key={d.dataset_id} className="border-b border-[color:var(--glass-border)] last:border-0">
                  <td className="py-1.5 text-[color:var(--ink-2)] truncate max-w-[180px]">{d.dataset_name}</td>
                  <td className="py-1.5 tabular-nums">{d.count}</td>
                  <td className="py-1.5 tabular-nums font-semibold">{d.avg_ms} ms</td>
                  <td className="py-1.5 tabular-nums text-[color:var(--ink-3)]">{d.max_ms} ms</td>
                </tr>
              ))}
              {!(summary?.slowest_datasets || []).length && <tr><td colSpan={4} className="py-5 text-center text-[color:var(--ink-3)]">No queries recorded in this window.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="glass-card p-4">
          <h4 className="text-xs font-semibold text-[color:var(--ink)] mb-2">Slowest individual queries (24h)</h4>
          <div className="overflow-auto max-h-72">
            <table className="w-full text-xs" data-testid="perf-recent-table">
              <thead><tr className="text-left text-[color:var(--ink-3)] border-b border-[color:var(--glass-border)]"><th className="py-1.5">Time</th><th className="py-1.5">Dataset</th><th className="py-1.5">Engine</th><th className="py-1.5">Rows</th><th className="py-1.5">Duration</th></tr></thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-b border-[color:var(--glass-border)] last:border-0">
                    <td className="py-1.5 text-[color:var(--ink-3)] whitespace-nowrap">{r.ts ? new Date(r.ts).toLocaleTimeString() : "—"}</td>
                    <td className="py-1.5 text-[color:var(--ink-2)] truncate max-w-[140px]">{r.dataset_name}</td>
                    <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{r.cached ? "cache" : r.engine}</Badge></td>
                    <td className="py-1.5 tabular-nums">{r.row_count}</td>
                    <td className="py-1.5 tabular-nums font-semibold">{r.duration_ms} ms</td>
                  </tr>
                ))}
                {!recent.length && <tr><td colSpan={5} className="py-5 text-center text-[color:var(--ink-3)]">Nothing yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Usage-based cache warming */}
      <div className="glass-card p-4" data-testid="perf-warm-panel">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-[color:var(--ink)] flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[color:var(--danger)]" />Usage-based cache warming</h4>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-[color:var(--ink-3)]">
              {warm.finished_at ? `Last run: ${new Date(warm.finished_at).toLocaleString()} · ${warm.last_result || "—"}` : "Not run yet"}
            </span>
            <Button size="sm" variant="outline" onClick={warmNow} disabled={warming} data-testid="perf-warm-now" className="bg-[color:var(--surface)]">
              {warming ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Flame className="w-3.5 h-3.5 mr-1" />}Warm now
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-[color:var(--ink-3)] mb-2">Every 15 min the scheduler re-runs the top ~25 queries executed ≥ 2× in the last day, so the query cache stays hot before users hit refresh cliffs. Runs as each original user so RLS-personalized results are preserved.</p>
        <div className="overflow-auto max-h-56">
          <table className="w-full text-xs" data-testid="perf-warm-table">
            <thead><tr className="text-left text-[color:var(--ink-3)] border-b border-[color:var(--glass-border)]"><th className="py-1.5">Dataset</th><th className="py-1.5">Recent runs</th><th className="py-1.5">Last used</th></tr></thead>
            <tbody>
              {(warm.targets || []).map((t, i) => (
                <tr key={`${t.dataset_id}-${t.user_id}-${i}`} className="border-b border-[color:var(--glass-border)] last:border-0">
                  <td className="py-1.5 text-[color:var(--ink-2)] truncate max-w-[240px]">{t.dataset_name || t.dataset_id}</td>
                  <td className="py-1.5 tabular-nums font-semibold">{t.count}</td>
                  <td className="py-1.5 text-[color:var(--ink-3)] whitespace-nowrap">{t.last_used ? new Date(t.last_used).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {!(warm.targets || []).length && <tr><td colSpan={3} className="py-5 text-center text-[color:var(--ink-3)]">No hot queries yet — run some governed dataset queries first.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
